import { useEffect } from "react";
import { createPortal } from "react-dom";
import { CloseIcon, DownloadIcon } from "./icons";

/** Full-screen in-app image viewer. Portalled above the chat overlay.
 *  Esc or backdrop click closes; the image itself doesn't propagate. */
export function Lightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 animate-[fadeIn_.15s_ease-out]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <a
          href={url}
          download
          onClick={(e) => e.stopPropagation()}
          className="icon-btn text-slate-200 bg-white/10 hover:bg-white/20"
          title="Download"
        >
          <DownloadIcon />
        </a>
        <button
          onClick={onClose}
          className="icon-btn text-slate-200 bg-white/10 hover:bg-white/20"
          title="Close"
        >
          <CloseIcon />
        </button>
      </div>
      <img
        src={url}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
      />
    </div>,
    document.body,
  );
}
